import * as React from 'react'
import { Combobox, Option } from 'uiComponents/combobox/combobox'
import { SegmentedControl, SegmentOption } from 'uiComponents/segmentedControl/segmentedControl'
import { useGetAllCategoriesForSelect, useGetAllProductsForSelect } from 'products/redux'
import { SearchKind, SearchSelection, STATUS_VALUES, StatusFilter } from './domain/listFilters'
import './listToolbar.scss'
import { useTranslation } from 'translations'

export type TimeSlotsView = 'categories' | 'products'

const optionValue = (kind: SearchKind, id: string) => `${kind}:${id}`

const parseValue = (value: string): SearchSelection => {
    const [kind, ...rest] = value.split(':')
    return { kind: kind as SearchKind, id: rest.join(':') }
}

const withKind = (option: Option, kind: SearchKind, kindLabel: string): Option => ({
    ...option,
    value: optionValue(kind, option.value),
    renderLabel: (
        <>
            {option.label}
            <span className="timeslots-search-kind">{kindLabel}</span>
        </>
    ),
})

export function useSearchOptions(): Option[] {
    const { t } = useTranslation()
    const categories = useGetAllCategoriesForSelect()
    const products = useGetAllProductsForSelect()
    return React.useMemo(
        () => [
            ...categories.map(({ label, value }) =>
                withKind({ label, value }, 'category', t('capacity.timeSlots.kind.category')),
            ),
            ...products.map((option) => withKind(option, 'product', t('capacity.timeSlots.kind.product'))),
        ],
        [categories, products, t],
    )
}

export const selectionLabel = (selection: SearchSelection | null, options: Option[]): string | null =>
    selection
        ? options.find((option) => option.value === optionValue(selection.kind, selection.id))?.label ?? null
        : null

interface TimeSlotsToolbarProps {
    options: Option[]
    optionsLoading: boolean
    selection: SearchSelection | null
    onSelect: (selection: SearchSelection | null) => void
    view: TimeSlotsView
    onViewChange: (view: TimeSlotsView) => void
    showViewSwitch: boolean
    viewBusy: boolean
    status: StatusFilter
    onStatusChange: (status: StatusFilter) => void
    counts: Record<StatusFilter, number>
}

export function TimeSlotsToolbar({
    options,
    optionsLoading,
    selection,
    onSelect,
    view,
    onViewChange,
    showViewSwitch,
    viewBusy,
    status,
    onStatusChange,
    counts,
}: TimeSlotsToolbarProps) {
    const { t } = useTranslation()
    const statusOptions = React.useMemo<SegmentOption<StatusFilter>[]>(
        () => [
            { value: 'all', label: `${t('capacity.timeSlots.all')} ${counts.all}` },
            ...STATUS_VALUES.map((value) => ({
                value: value as StatusFilter,
                label: `${t(`capacity.timeSlots.status.${value}`)} ${counts[value]}`,
            })),
        ],
        [counts],
    )
    const viewOptions: SegmentOption<TimeSlotsView>[] = [
        {
            value: 'categories',
            label: t('capacity.timeSlots.views.categories'),
            disabled: viewBusy,
            disabledReason: t('capacity.timeSlots.viewBusy'),
        },
        {
            value: 'products',
            label: t('capacity.timeSlots.views.products'),
            disabled: viewBusy,
            disabledReason: t('capacity.timeSlots.viewBusy'),
        },
    ]

    return (
        <div className="timeslots-toolbar">
            <div className="timeslots-search">
                <Combobox
                    options={options}
                    loading={optionsLoading}
                    value={selection ? optionValue(selection.kind, selection.id) : null}
                    searchPlaceholder={t('capacity.timeSlots.search')}
                    onChange={(next) => onSelect(typeof next === 'string' ? parseValue(next) : null)}
                />
            </div>
            <div className="timeslots-toolbar-controls">
                {showViewSwitch && (
                    <SegmentedControl
                        size="compact"
                        fit
                        ariaLabel={t('capacity.timeSlots.viewLabel')}
                        options={viewOptions}
                        value={view}
                        onChange={onViewChange}
                    />
                )}
                <SegmentedControl
                    size="compact"
                    fit
                    ariaLabel={t('capacity.timeSlots.statusLabel')}
                    options={statusOptions}
                    value={status}
                    onChange={onStatusChange}
                />
            </div>
        </div>
    )
}
